/**
 * Core value types for the terminal cell model.
 *
 * Colors are stored as packed 24-bit RGB integers (0xRRGGBB) regardless of the
 * document color mode. Quantisation to ANSI16 / ANSI256 happens only at the
 * export / preview boundary, which keeps the document lossless and makes
 * switching color modes a non-destructive operation.
 */

/** Packed 0xRRGGBB value, or {@link TRANSPARENT}. */
export type Color = number;

/** Sentinel meaning "no color of my own" (terminal default / show layer below). */
export const TRANSPARENT: Color = -1;

export const Attr = {
  NONE: 0,
  BOLD: 1 << 0,
  DIM: 1 << 1,
  ITALIC: 1 << 2,
  UNDERLINE: 1 << 3,
  BLINK: 1 << 4,
  INVERSE: 1 << 5,
  HIDDEN: 1 << 6,
  STRIKE: 1 << 7,
} as const;

export type AttrMask = number;

/** The public, immutable view of a single terminal cell. */
export interface Cell {
  /** Grapheme cluster. Empty string means "cell is unpainted". */
  readonly char: string;
  readonly fg: Color;
  readonly bg: Color;
  readonly attrs: AttrMask;
}

export const EMPTY_CELL: Cell = Object.freeze({
  char: '',
  fg: TRANSPARENT,
  bg: TRANSPARENT,
  attrs: Attr.NONE,
});

export const SPACE_CELL: Cell = Object.freeze({
  char: ' ',
  fg: TRANSPARENT,
  bg: TRANSPARENT,
  attrs: Attr.NONE,
});

export type ColorMode = 'ansi16' | 'ansi256' | 'truecolor';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export function rect(x: number, y: number, width: number, height: number): Rect {
  return { x, y, width, height };
}

export function rectFromPoints(a: Point, b: Point): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.abs(a.x - b.x) + 1, height: Math.abs(a.y - b.y) + 1 };
}

export function rectContains(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.width && y < r.y + r.height;
}

export function rectIntersect(a: Rect, b: Rect): Rect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

export function rectUnion(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b;
  if (!b) return a;
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

export function rectEquals(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

export function cellEquals(a: Cell, b: Cell): boolean {
  return a.char === b.char && a.fg === b.fg && a.bg === b.bg && a.attrs === b.attrs;
}

export function rgb(r: number, g: number, b: number): Color {
  return ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);
}

export function unpackRgb(c: Color): [number, number, number] {
  return [(c >> 16) & 0xff, (c >> 8) & 0xff, c & 0xff];
}

export function colorToHex(c: Color): string {
  if (c === TRANSPARENT) return 'transparent';
  return '#' + (c & 0xffffff).toString(16).padStart(6, '0');
}

export function hexToColor(hex: string): Color {
  const s = hex.trim().replace(/^#/, '');
  if (s.length === 3) {
    const r = parseInt(s[0] + s[0], 16);
    const g = parseInt(s[1] + s[1], 16);
    const b = parseInt(s[2] + s[2], 16);
    return rgb(r, g, b);
  }
  const v = parseInt(s.slice(0, 6), 16);
  return Number.isNaN(v) ? 0 : v & 0xffffff;
}
